import React, { createContext, useContext, useState } from 'react';

// --- THEME ENGINE CONFIGURATION ---
const themeConfig = {
    original: {
        wrapper: "bg-gradient-to-b from-[#f9f9f9] to-white font-sans text-black",
        container: "bg-white border border-black/10 rounded-xl shadow-sm max-w-[768px]",
        text: {
            primary: "text-black"
        },
        header: {
            avatar: "bg-gradient-to-br from-neutral-700 via-neutral-900 to-black rounded-full text-white ring-1 ring-white/10",
            text: "text-black font-medium lowercase tracking-tight",
            icon: "bg-black text-white rounded-full hover:bg-gray-800 hover:scale-110 transition-all duration-200"
        },
        chat: {
            userBubble: "bg-[#f0f0f0] text-black rounded-xl",
            botBubble: "bg-white text-black border border-black/10 rounded-xl",
            actionBtn: "bg-white text-black border border-black/15 rounded-lg hover:bg-gray-50 font-normal",
            actionBtnSelected: "bg-black text-white border border-black rounded-lg",
            card: "bg-white border border-black/10 rounded-xl shadow-sm"
        },
        button: {
            option: "bg-white text-black border border-black/15 hover:bg-gray-50"
        },
        input: {
            container: "bg-white border-t border-black/10 rounded-b-xl",
            field: "text-black placeholder-gray-400",
            sendBtn: "text-black hover:bg-gray-100 rounded-lg"
        },
        fab: "bg-black text-white shadow-lg hover:scale-105 rounded-full",
        about: {
            background: "bg-gradient-to-b from-[#f9f9f9] to-white",
            container: "bg-white border border-black/10 rounded-xl shadow-sm",
            text: "text-gray-800",
            title: "text-black font-medium",
            backButton: "bg-white border border-black/10 text-black hover:bg-gray-50"
        }
    },
    clay: {
        wrapper: "bg-[#e6eef7] font-sans text-slate-700",
        container: "bg-[#e6eef7] rounded-[40px] shadow-[25px_25px_75px_#b8c6db,-25px_-25px_75px_#ffffff] border-none max-w-[768px]",
        text: {
            primary: "text-slate-700"
        },
        header: {
            avatar: "bg-[#e6eef7] shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff] rounded-full text-slate-700",
            text: "text-slate-700 font-bold tracking-wide",
            icon: "text-slate-500 hover:text-[#3b82f6] transition-colors"
        },
        chat: {
            userBubble: "bg-[#3b82f6] text-white rounded-[24px] rounded-tr-none shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff,inset_2px_2px_4px_rgba(255,255,255,0.3)] bg-gradient-to-br from-blue-400 to-blue-600",
            botBubble: "bg-[#e6eef7] text-slate-700 rounded-[24px] rounded-tl-none shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff]",
            actionBtn: "bg-[#e6eef7] text-slate-700 rounded-full shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff] hover:translate-y-[-2px] active:scale-95 transition-all border-none",
            actionBtnSelected: "bg-[#3b82f6] text-white shadow-[inset_4px_4px_8px_#2563eb,inset_-4px_-4px_8px_#60a5fa]",
            card: "bg-[#e6eef7] rounded-[30px] shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff] p-6 border-none"
        },
        button: {
            option: "bg-[#e6eef7] text-slate-600 rounded-[50px] shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff] hover:text-[#3b82f6] hover:translate-y-[-2px] active:scale-95 transition-all border-none"
        },
        input: {
            container: "bg-transparent border-t border-slate-200/50",
            field: "bg-[#e6eef7] text-slate-700 rounded-[50px] shadow-[inset_6px_6px_12px_#b8c6db,inset_-6px_-6px_12px_#ffffff] border-none placeholder-slate-400 px-6",
            sendBtn: "bg-[#3b82f6] text-white rounded-full shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff,inset_2px_2px_4px_rgba(255,255,255,0.3)] hover:translate-y-[-2px] active:scale-95 transition-all bg-gradient-to-br from-blue-400 to-blue-600"
        },
        fab: "bg-[#e6eef7] text-[#3b82f6] shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff] rounded-full hover:translate-y-[-2px] active:scale-95",
        about: {
            background: "bg-[#e6eef7]",
            container: "bg-[#e6eef7] rounded-[40px] shadow-[25px_25px_75px_#b8c6db,-25px_-25px_75px_#ffffff] border-none",
            text: "text-slate-700",
            title: "text-slate-800 font-bold",
            backButton: "bg-[#e6eef7] text-slate-600 rounded-[50px] shadow-[8px_8px_16px_#b8c6db,-8px_-8px_16px_#ffffff] hover:text-[#3b82f6]"
        }
    },
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState('original');

    const cycleTheme = () => {
        const themes = ['original', 'clay'];
        const currentIndex = themes.indexOf(currentTheme);
        const nextIndex = (currentIndex + 1) % themes.length;
        setCurrentTheme(themes[nextIndex]);
    };

    const theme = themeConfig[currentTheme];

    return (
        <ThemeContext.Provider value={{ currentTheme, cycleTheme, theme, themeConfig }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
